import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Avatar } from "@/components/Avatar";
import type { Notification } from "@/messaging/types";
import {
  BellIcon,
  ExternalLinkIcon,
  InboxIcon,
  MegaphoneIcon,
  SmileIcon,
} from "@/messaging/chat/icons";
import { useNotifications } from "./NotificationsContext";
import {
  announcementId,
  dayBucket,
  safeLink,
  timeAgo,
  unusedQuickReactions,
} from "./shared";

const PANEL_W = 380;
/** Gap between the bell and the panel, and the minimum breathing room we
 *  keep from the viewport edges when clamping. */
const GAP = 8;

export function NotificationsBell() {
  const {
    unread,
    items,
    listState,
    panelOwner,
    canBroadcast,
    togglePanel,
    closePanel,
    registerAnchor,
    setSelected,
    setComposeOpen,
    markRead,
    markAllRead,
    react,
  } = useNotifications();

  // Stable per-instance id. The topbar mounts this component twice (mobile
  // + desktop headers, one hidden by CSS at any breakpoint); only the
  // instance the user actually clicked owns the panel.
  const instanceId = useId();
  const isOpen = panelOwner === instanceId;

  const navigate = useNavigate();
  const btnRef = useRef<HTMLButtonElement | null>(null);

  const openNotification = (n: Notification) => {
    markRead(n.id);
    if (announcementId(n)) {
      setSelected(n);
      closePanel();
      return;
    }
    const href = safeLink(n.link_url);
    if (!href) return; // nothing to go to — the row just marks itself read
    closePanel();
    if (href.startsWith("/")) navigate(href);
    else window.open(href, "_blank", "noopener");
  };

  return (
    <div className="relative" ref={registerAnchor}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => togglePanel(instanceId)}
        className={`icon-btn ${isOpen ? "icon-btn-active" : ""}`}
        title={unread > 0 ? `${unread} unread` : "Notifications"}
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        <BellIcon />
        {unread > 0 && (
          <span className="icon-badge bg-accent-500 text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {isOpen && (
        <NotificationsPanel
          anchorRef={btnRef}
          unread={unread}
          items={items}
          listState={listState}
          canBroadcast={canBroadcast}
          onCompose={() => {
            closePanel();
            setComposeOpen(true);
          }}
          onMarkAllRead={markAllRead}
          onOpenNotification={openNotification}
          onReact={react}
        />
      )}
    </div>
  );
}

/** The dropdown itself, portaled to <body>.
 *
 *  It can't live inside the topbar: both headers are `sticky … z-30`, which
 *  creates a stacking context, so any z-index the panel sets is trapped
 *  below 30 and the chat drawer (z-[1100]) paints straight over it. Portaling
 *  and positioning from the button's rect fixes that and lets the panel
 *  overflow the header instead of being clipped by it. */
function NotificationsPanel({
  anchorRef,
  unread,
  items,
  listState,
  canBroadcast,
  onCompose,
  onMarkAllRead,
  onOpenNotification,
  onReact,
}: {
  anchorRef: React.RefObject<HTMLButtonElement>;
  unread: number;
  items: Notification[] | null;
  listState: "idle" | "loading" | "ready" | "error";
  canBroadcast: boolean;
  onCompose: () => void;
  onMarkAllRead: () => void;
  onOpenNotification: (n: Notification) => void;
  onReact: (announcementId: string, emoji: string) => void;
}) {
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.min(PANEL_W, window.innerWidth - GAP * 2);
    // Right-aligned to the bell, then clamped so a bell near either edge
    // (the mobile header sits tight against the right) stays on screen.
    const left = Math.min(
      Math.max(GAP, r.right - width),
      window.innerWidth - width - GAP,
    );
    setPos({ top: r.bottom + GAP, left, width });
  }, [anchorRef]);

  useLayoutEffect(place, [place]);

  useEffect(() => {
    // `true` so scrolls inside any container (not just the window) reposition.
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [place]);

  // Unread first, then newest — an unread item from yesterday still
  // deserves the top of the list over a read one from this morning.
  const sorted = useMemo(() => {
    if (!items) return [];
    return [...items].sort((a, b) => {
      const au = a.read_at ? 1 : 0;
      const bu = b.read_at ? 1 : 0;
      if (au !== bu) return au - bu;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [items]);

  // Unread items lead as one block under a single "Unread" header; only the
  // already-read tail is bucketed by day. Bucketing the whole list instead
  // would interleave the two orderings and emit the same day header twice
  // (unread-Today, unread-Yesterday, then read-Today).
  const withHeaders = useMemo(() => {
    const out: Array<{ header: string } | { n: Notification }> = [];
    let last: string | null = null;
    for (const n of sorted) {
      const b = n.read_at ? dayBucket(n.created_at) : "Unread";
      if (b !== last) {
        out.push({ header: b });
        last = b;
      }
      out.push({ n });
    }
    return out;
  }, [sorted]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      data-notif-panel
      style={
        pos
          ? { top: pos.top, left: pos.left, width: pos.width }
          : { top: -9999, left: -9999, width: PANEL_W }
      }
      className="fixed z-[1300] flex max-h-[min(72vh,560px)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-ink-900/95 shadow-2xl shadow-black/50 backdrop-blur-md animate-[fadeIn_.12s_ease-out]"
      role="dialog"
      aria-label="Notifications"
    >
      <div className="flex items-center justify-between border-b border-white/5 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-100">Notifications</span>
          {unread > 0 && (
            <span className="rounded-full bg-accent-500/15 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-accent-300">
              {unread}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {canBroadcast && (
            <button
              type="button"
              onClick={onCompose}
              className="inline-flex items-center gap-1 rounded-lg bg-accent-600 px-2.5 py-1 text-[11px] font-semibold text-white transition hover:bg-accent-500"
              title="New announcement"
            >
              <MegaphoneIcon size={14} /> Announce
            </button>
          )}
          {unread > 0 && (
            <button
              type="button"
              onClick={onMarkAllRead}
              className="rounded-lg px-2 py-1 text-[11px] text-slate-500 transition hover:text-accent-300"
            >
              Mark all read
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
        {listState === "loading" && items === null && (
          <div className="space-y-2 p-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-2.5">
                <div className="h-8 w-8 flex-shrink-0 rounded-full bg-white/[0.04] animate-pulse" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-2/3 rounded bg-white/[0.04] animate-pulse" />
                  <div className="h-2.5 w-1/2 rounded bg-white/[0.03] animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        )}

        {listState === "error" && (
          <div className="px-4 py-10 text-center text-xs text-slate-500">
            Couldn't load notifications.
          </div>
        )}

        {(listState === "ready" || items) && sorted.length === 0 && (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center text-slate-500">
            <InboxIcon size={30} />
            <div className="text-sm font-medium text-slate-400">You're all caught up</div>
            <div className="text-xs">New notifications will show up here.</div>
          </div>
        )}

        {withHeaders.map((entry) =>
          "header" in entry ? (
            <div
              key={`h-${entry.header}`}
              className="sticky top-0 z-10 bg-ink-900/95 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 backdrop-blur-md"
            >
              {entry.header}
            </div>
          ) : (
            <NotificationRow
              key={entry.n.id}
              n={entry.n}
              onOpen={() => onOpenNotification(entry.n)}
              onReact={(emoji) => {
                const aid = announcementId(entry.n);
                if (aid) onReact(aid, emoji);
              }}
            />
          ),
        )}
      </div>
    </div>,
    document.body,
  );
}

function NotificationRow({
  n,
  onOpen,
  onReact,
}: {
  n: Notification;
  onOpen: () => void;
  onReact: (emoji: string) => void;
}) {
  // Expanded inline rather than in an absolutely-positioned popup: the list
  // scrolls inside an overflow container, which clipped the old popup for
  // any row near the bottom and left it unreachable.
  const [picking, setPicking] = useState(false);
  const isAnnouncement = !!announcementId(n);
  const sender = n.payload as {
    sender_name?: string;
    sender_email?: string;
    sender_avatar_url?: string | null;
  };
  const unread = !n.read_at;
  const quick = unusedQuickReactions(n);
  const href = safeLink(n.link_url);

  return (
    <div
      className={`relative border-b border-white/[0.04] transition ${
        isAnnouncement ? "bg-accent-500/[0.04]" : ""
      } ${unread ? "bg-accent-500/[0.07]" : ""} hover:bg-white/[0.03]`}
    >
      {(unread || isAnnouncement) && (
        <span
          className={`absolute left-0 top-0 h-full w-0.5 ${
            unread ? "bg-accent-500" : "bg-accent-500/25"
          }`}
        />
      )}
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full gap-2.5 px-3.5 py-3 text-left"
      >
        {isAnnouncement ? (
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent-500/15 text-accent-300">
            <MegaphoneIcon size={16} />
          </span>
        ) : (
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/[0.05] text-slate-400">
            <BellIcon size={16} />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <span
              className={`text-[13px] leading-snug ${
                unread ? "font-semibold text-slate-100" : "text-slate-200"
              }`}
            >
              {n.title}
            </span>
            {unread && (
              <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent-400" />
            )}
          </div>
          {n.body && <div className="mt-0.5 line-clamp-2 text-[12px] text-slate-400">{n.body}</div>}
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
            {isAnnouncement && sender.sender_name && (
              <>
                <Avatar
                  url={sender.sender_avatar_url}
                  name={sender.sender_name}
                  email={sender.sender_email}
                  size="xs"
                  className="!h-4 !w-4"
                />
                <span className="truncate">{sender.sender_name}</span>
                <span>·</span>
              </>
            )}
            <span>{timeAgo(n.created_at)}</span>
            {href && !isAnnouncement && (
              <ExternalLinkIcon size={12} className="text-slate-600" />
            )}
          </div>
        </div>
      </button>

      {isAnnouncement && (
        <div className="flex flex-wrap items-center gap-1 px-3.5 pb-2.5 pl-[42px]">
          {n.reactions.map((r) => (
            <button
              key={r.emoji}
              type="button"
              onClick={() => onReact(r.emoji)}
              title={r.users.map((u) => u.full_name || u.email).join(", ")}
              className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] transition ${
                r.me
                  ? "border-accent-500/50 bg-accent-500/15 text-accent-200"
                  : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/20"
              }`}
            >
              <span className="text-[12px] leading-none">{r.emoji}</span>
              <span className="tabular-nums">{r.count}</span>
            </button>
          ))}
          {picking ? (
            quick.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => {
                  onReact(e);
                  setPicking(false);
                }}
                className="h-6 w-6 rounded-full text-[15px] leading-none transition hover:bg-white/10"
              >
                {e}
              </button>
            ))
          ) : (
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="icon-btn h-6 w-6"
              title="React"
              aria-label="React"
            >
              <SmileIcon size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
